import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import Calendar from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';

interface DatePickerFieldProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  label: string;
  minDate?: Date;
  maxDate?: Date;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Formatul afisat pe buton (token-uri date-fns). Implicit 'PPP' (text lung, romanesc). */
  displayFormat?: string;
}

/**
 * DatePickerField - Reusable date picker component
 *
 * Features:
 * - shadcn Calendar integration
 * - Romanian locale support
 * - Min/max date validation
 * - Integration with react-hook-form via Controller
 * - Design system styling
 *
 * ⚠️ NU foloseste `FormItem`/`FormLabel`/`FormControl`/`FormMessage` din
 * `@/components/ui/form` (SE-30, 2026-09-16): toate patru cheama `useFormField()`,
 * care apeleaza NECONDITIONAT `useFormContext()` din react-hook-form — fara un
 * `<FormProvider>` (`<Form {...form}>`) in arbore, contextul e `null`, iar
 * destructurarea arunca la randare. `DatePickerField` e documentata ca reutilizabila,
 * dar era de fapt cuplata la un formular RHF montat undeva deasupra — invizibil cat
 * timp singurii consumatori erau in interiorul unui `<Form>`. Primul consumator FARA
 * react-hook-form (`TableDateRangeFilter`, filtru de coloana intr-un tabel de raport,
 * condus de `handleChange`, nu de un `Controller`) ar fi picat la prima randare.
 * Markup-ul de mai jos e echivalent vizual (aceleasi clase), doar fara dependenta
 * ascunsa de context — identic pentru cei doi consumatori RHF existenti.
 *
 * Context7 patterns:
 * - Controller for custom component integration
 *
 * @example
 * <Controller
 *   control={form.control}
 *   name="dataOrientativaColectare"
 *   render={({ field, fieldState }) => (
 *     <DatePickerField
 *       value={field.value}
 *       onChange={field.onChange}
 *       label="Data Orientativă Colectare:"
 *       minDate={minPickupDate}
 *       error={fieldState.error?.message}
 *       disabled={loading}
 *     />
 *   )}
 * />
 */
export function DatePickerField({
  value,
  onChange,
  label,
  minDate,
  maxDate,
  error,
  disabled = false,
  placeholder = 'Selectați data',
  displayFormat = 'PPP',
}: DatePickerFieldProps) {
  const id = React.useId();
  const messageId = `${id}-message`;

  return (
    <div className="form-field-row">
      <Label htmlFor={id} className="form-label-bold">{label}</Label>
      <div className="flex-1 space-y-0">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id={id}
              variant="outline"
              disabled={disabled}
              aria-invalid={!!error}
              aria-describedby={error ? messageId : undefined}
              /* `border-input` explicit: `form-input-styled` NU mai coloreaza
                 chenarul (vezi comentariul din main.css), iar `Button` nu aduce
                 nicio culoare — cadea pe gray-300, 1,47. */
              className={`
                w-full justify-start text-left font-normal
                form-input-styled border-input
                ${!value && 'text-muted-foreground'}
              `}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {value ? (
                format(value, displayFormat, { locale: ro })
              ) : (
                <span>{placeholder}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={value}
              onSelect={onChange}
              disabled={(date) => {
                if (minDate && date < minDate) return true;
                if (maxDate && date > maxDate) return true;
                return false;
              }}
              initialFocus
              locale={ro}
            />
          </PopoverContent>
        </Popover>
        {error && (
          <p id={messageId} role="alert" className="form-message-spacer text-destructive text-sm">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
